import { escapeReferenceToken } from '@criteria/json-pointer';
import deepEqual from 'deep-equal';
import { Attribute } from './attribute';
function addAttributes(array, others) {
    for (const other of others) {
        const matchedIndex = array.findIndex((attribute) => attribute.canCombine(other));
        if (matchedIndex >= 0) {
            array[matchedIndex] = array[matchedIndex].combine(other);
        }
        else {
            array.push(other);
        }
    }
}
// TODO: factor into attributesContainerSchemaForSchema and attributesFromContainerSchema
export function schemaHasAttributes(schema) {
    var _a, _b;
    if (typeof schema === 'boolean') {
        return false;
    }
    // Special case for Bloc's API that uses an allOf array to merge example with main schema
    if (deepEqual(Object.keys(schema), ['allOf']) &&
        ((_a = schema.allOf) === null || _a === void 0 ? void 0 : _a.length) === 2 &&
        typeof schema.allOf[1] === 'object' &&
        typeof schema.allOf[1] !== null &&
        deepEqual(Object.keys(schema.allOf[1]), ['example'])) {
        return schemaHasAttributes(schema.allOf[0]);
    }
    if (schema.type === 'object') {
        return true;
    }
    if (schema.type === 'array') {
        // Flatten items if possible
        if (schema.allOf === undefined && schema.prefixItems === undefined) {
            if (schema.items === undefined) {
                return false;
            }
            else {
                return schemaHasAttributes(schema.items);
            }
        }
        return true;
    }
    if (Array.isArray(schema.type)) {
        if (schema.type.includes('object') || schema.type.includes('array')) {
            return true;
        }
    }
    if ('allOf' in schema) {
        if ((_b = schema.allOf) === null || _b === void 0 ? void 0 : _b.some((subschema) => schemaHasAttributes(subschema))) {
            return true;
        }
    }
    return false;
}
export function attributesFromSchema(schema) {
    var _a;
    if (typeof schema === 'boolean') {
        return {
            attributes: [],
            attributesContainerSchema: schema,
            attributesContainerSchemaLocation: ''
        };
    }
    // Special case for Bloc's API that uses an allOf array to merge example with main schema
    if (deepEqual(Object.keys(schema), ['allOf']) &&
        ((_a = schema.allOf) === null || _a === void 0 ? void 0 : _a.length) === 2 &&
        typeof schema.allOf[1] === 'object' &&
        typeof schema.allOf[1] !== null &&
        deepEqual(Object.keys(schema.allOf[1]), ['example'])) {
        const subschemaAttributes = attributesFromSchema(schema.allOf[0]);
        return {
            attributes: subschemaAttributes.attributes,
            attributesContainerSchema: subschemaAttributes.attributesContainerSchema,
            attributesContainerSchemaLocation: `/allOf/0${subschemaAttributes.attributesContainerSchemaLocation}`
        };
    }
    if (schema.type === 'array') {
        return arrayAttributesFromSchema(schema);
    }
    return objectAttributesFromSchema(schema);
}
function attributesFromAllOf(schema) {
    const { allOf } = schema;
    if (allOf === undefined) {
        return [];
    }
    const attributes = [];
    for (let index = 0; index < allOf.length; index++) {
        const subschema = allOf[index];
        const subschemaAttributes = attributesFromSchema(subschema).attributes.map((attribute) => attribute.withValueSchemaLocation(`/allOf/${index}${attribute.valueSchemaLocation}`));
        addAttributes(attributes, subschemaAttributes);
    }
    return attributes;
}
function objectAttributesFromSchema(schema) {
    const { allOf, 
    // applicator
    properties, patternProperties, additionalProperties, propertyNames, 
    // validation
    maxProperties, minProperties, required, dependentRequired, 
    // unevaluated
    unevaluatedProperties } = schema;
    const attributes = [];
    if (allOf !== undefined) {
        addAttributes(attributes, attributesFromAllOf(schema));
    }
    if (properties !== undefined) {
        addAttributes(attributes, attributesFromProperties(schema));
    }
    if (patternProperties !== undefined) {
        addAttributes(attributes, attributesFromPatternProperties(schema));
    }
    if (additionalProperties !== undefined) {
        addAttributes(attributes, attributesFromAdditionalProperties(schema));
    }
    return {
        attributes,
        attributesContainerSchema: schema,
        attributesContainerSchemaLocation: ''
    };
}
function attributesFromProperties(schema) {
    var _a;
    const { required, properties } = schema;
    if (properties === undefined) {
        return [];
    }
    const attributes = [];
    for (const name in properties) {
        const optional = !((_a = required === null || required === void 0 ? void 0 : required.includes(name)) !== null && _a !== void 0 ? _a : false);
        const schemaLocation = `/properties/${escapeReferenceToken(name)}`;
        const attribute = new Attribute(name, optional, properties[name], schemaLocation, false);
        attributes.push(attribute);
    }
    return attributes;
}
function attributesFromPatternProperties(schema) {
    const { patternProperties } = schema;
    if (patternProperties === undefined) {
        return [];
    }
    const attributes = [];
    for (const pattern in patternProperties) {
        const schemaLocation = `/patternProperties/${escapeReferenceToken(pattern)}`;
        const attribute = new Attribute(pattern, true, patternProperties[pattern], schemaLocation, true);
        attributes.push(attribute);
    }
    return attributes;
}
function attributesFromAdditionalProperties(schema) {
    const { additionalProperties } = schema;
    if (additionalProperties === undefined) {
        return [];
    }
    const attributes = [];
    if (additionalProperties !== false) {
        const attribute = new Attribute('', true, additionalProperties, '/additionalProperties', true);
        attributes.push(attribute);
    }
    return attributes;
}
function arrayAttributesFromSchema(schema) {
    const { allOf, prefixItems, items, contains, maxItems, minItems, uniqueItems, maxContains, minContains, unevaluatedItems } = schema;
    // Flatten items if possible
    if (allOf === undefined && prefixItems === undefined) {
        if (items === undefined) {
            return {
                attributes: [],
                attributesContainerSchema: null,
                attributesContainerSchemaLocation: '/items'
            };
        }
        const itemsAttributes = attributesFromSchema(items);
        return {
            attributes: itemsAttributes.attributes,
            attributesContainerSchema: itemsAttributes.attributesContainerSchema,
            attributesContainerSchemaLocation: `/items${itemsAttributes.attributesContainerSchemaLocation}`
        };
    }
    const attributes = [];
    if (allOf !== undefined) {
        addAttributes(attributes, attributesFromAllOf(schema));
    }
    if (prefixItems !== undefined) {
        addAttributes(attributes, attributesFromPrefixItems(schema));
    }
    if (items !== undefined) {
        addAttributes(attributes, attributesFromItems(schema));
    }
    return {
        attributes,
        attributesContainerSchema: schema,
        attributesContainerSchemaLocation: ''
    };
}
function attributesFromPrefixItems(schema) {
    const { prefixItems } = schema;
    if (prefixItems === undefined) {
        return [];
    }
    const attributes = [];
    for (let index = 0; index < prefixItems.length; index++) {
        const prefixItemSchema = prefixItems[index];
        const attribute = new Attribute(`[${index}]`, true, prefixItemSchema, `/prefixItems/${index}`, false);
        attributes.push(attribute);
    }
    return attributes;
}
function attributesFromItems(schema) {
    const { items } = schema;
    if (items === undefined) {
        return [];
    }
    const attributes = [];
    const attribute = new Attribute('[]', true, items, '/items', false);
    attributes.push(attribute);
    return attributes;
}
